import type { KeyResult } from '../models/keyResult.types';

/**
 * Calculates Objective progress as arithmetic mean of all Key Results with capping
 *
 * Uses equal-weight averaging where each Key Result contributes equally
 * to the overall Objective progress, regardless of the number of Key Results.
 *
 * **IMPORTANT**: Each KR's contribution is capped at 100% to prevent over-achievement
 * on one KR from masking underperformance on others. This encourages balanced progress.
 *
 * @param keyResults - Array of Key Results belonging to the Objective
 * @returns Aggregated progress percentage (0-100, whole number)
 *
 * @example
 * // Single KR exceeding target
 * calculateObjectiveProgress([{ progress: 150 }]) // Returns: 100 (capped)
 *
 * @example
 * // Multiple KRs with one exceeding
 * calculateObjectiveProgress([
 *   { progress: 120 }, // Capped to 100
 *   { progress: 80 },
 *   { progress: 50 }
 * ]) // Returns: 77 (from (100 + 80 + 50) / 3 = 76.67)
 *
 * @example
 * // All KRs under 100% (no capping)
 * calculateObjectiveProgress([
 *   { progress: 20 },
 *   { progress: 40 },
 *   { progress: 80 }
 * ]) // Returns: 47 (from 46.67)
 *
 * @example
 * // Empty array
 * calculateObjectiveProgress([]) // Returns: 0
 */
export function calculateObjectiveProgress(keyResults: KeyResult[]): number {
  // Edge case: no Key Results
  if (keyResults.length === 0) {
    return 0;
  }

  // Calculate sum of all KR progress values with capping at 100%
  // This prevents over-achievement on one KR from masking underperformance on others
  const cappedSum = keyResults.reduce((acc, kr) => {
    return acc + Math.min(kr.progress, 100);
  }, 0);

  // Calculate arithmetic mean
  const average = cappedSum / keyResults.length;

  // Round to whole number for display clarity
  return Math.round(average);
}
